import { useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@contexts/Toast.context';
import {
	DEFAULT_HEXAGON_COLOR,
	DEFAULT_HEXAGON_RESOLUTION,
	useCollectionStore,
} from '@stores/useCollectionStore';
import { updateCollection } from '@utils/api/collections';
import {
	COLLECTION_CONFIG_SCOPE,
	collectionConfigKey,
	invalidateCollectionConfig,
} from '@utils/collectionQueries';

import { HEXAGON_RESOLUTION_MAX, HEXAGON_RESOLUTION_MIN } from '../constants';
import { toHexColor } from '../utils/transformUtils';

/**
 * The collection's stored `hexagon` field: reading it, seeding the store from
 * it, and writing it back. The field is free-form — the server stores and
 * returns it without inspecting it — so nothing but this module decides what a
 * valid value is. The settings themselves stay in `useCollectionStore`.
 */

/**
 * Normalize the stored `hexagon` object into usable settings, falling back per
 * field. Nothing is trusted: a collection saved before the field existed
 * returns `{}`, an unusable colour renders the layer grey (`hexToRgba` takes
 * 6-digit hex only), and a size outside the input's own bounds would reach the
 * aggregation endpoint.
 *
 * @param {object} [hexagon] - raw `collection.hexagon`
 * @returns {{ size: number, color: string }}
 */
export const readHexagonConfig = hexagon => {
	const size = Number(hexagon?.size);
	const color = toHexColor(hexagon?.color);

	return {
		size:
			Number.isFinite(size) &&
			size >= HEXAGON_RESOLUTION_MIN &&
			size <= HEXAGON_RESOLUTION_MAX
				? size
				: DEFAULT_HEXAGON_RESOLUTION,
		// toHexColor also collapses #rgb and hsl(), but keeps 8-digit #rrggbbaa
		// — which hexToRgba rejects — so alpha is dropped rather than trusted.
		color: color?.length === 7 ? color : DEFAULT_HEXAGON_COLOR,
	};
};

/**
 * Seeds the store from the collection's saved settings, once per selection.
 *
 * Must be called *after* `useCollectionTree`: its effect calls the store's
 * `reset()` on a collection switch, and effects run in call order, so hydrating
 * second seeds the new values instead of having them wiped.
 *
 * The marker lives in the store so that same `reset()` clears it — in local
 * state it could outlive a reset and leave the store on defaults while
 * claiming to be seeded. Re-seeding within one selection must not happen: a
 * later config refetch carries the same field and would fight a value a save
 * just applied.
 *
 * @param {string} collectionUuid
 * @param {{ size: number, color: string }} [hexagonConfig] - from useCollectionData
 * @returns {boolean} whether the store now holds this collection's saved settings
 */
export const useHexagonConfigHydration = (collectionUuid, hexagonConfig) => {
	const setHexagonResolution = useCollectionStore(s => s.setHexagonResolution);
	const setHexagonColor = useCollectionStore(s => s.setHexagonColor);
	const hydratedUuid = useCollectionStore(s => s.hydratedCollectionUuid);
	const setHydratedUuid = useCollectionStore(s => s.setHydratedCollectionUuid);

	const isHydrated = !!collectionUuid && hydratedUuid === collectionUuid;

	useEffect(() => {
		if (!collectionUuid || !hexagonConfig || isHydrated) return;
		setHexagonResolution(hexagonConfig.size);
		setHexagonColor(hexagonConfig.color);
		setHydratedUuid(collectionUuid);
	}, [
		collectionUuid,
		hexagonConfig,
		isHydrated,
		setHexagonResolution,
		setHexagonColor,
		setHydratedUuid,
	]);

	return isHydrated;
};

/**
 * The one write path for the collection's `hexagon` settings.
 *
 * **Saves first, then applies.** The store is what the map and the query key
 * render from, and it only moves once the PATCH has landed — so the map can
 * never show a value the collection does not hold, and a failed save leaves
 * the screen untouched.
 *
 * PATCH replaces the whole object, so each save merges over what is cached
 * rather than rebuilding it — the rule `detectionObjectsPayload.js` documents
 * for `style`. `COLLECTION_CONFIG_SCOPE` is shared with the `detection_objects`
 * writes so React Query serialises them, and the confirmed value goes into the
 * cache on success so a queued save merges onto it, not a stale snapshot.
 *
 * @param {string} collectionUuid
 * @returns {{ saveHexagonConfig: (patch: object) => void, isSaving: boolean }}
 */
export const useHexagonConfigWrite = collectionUuid => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const setHexagonResolution = useCollectionStore(s => s.setHexagonResolution);
	const setHexagonColor = useCollectionStore(s => s.setHexagonColor);
	const configKey = collectionConfigKey(collectionUuid);

	const saveMutation = useMutation({
		scope: COLLECTION_CONFIG_SCOPE(collectionUuid),

		mutationFn: async patch => {
			const cached = queryClient.getQueryData(configKey);
			if (!cached) {
				// The config query is loaded before any of these controls mount
				// (CollectionResults gates the whole layer on it), so an empty
				// cache means something upstream is wrong. Sending a PATCH built
				// from the patch alone would drop every setting it didn't name.
				throw new Error('Cannot save hexagon settings: collection config is not loaded');
			}
			const hexagon = { ...(cached.hexagon ?? {}), ...patch };
			await updateCollection(collectionUuid, { hexagon });
			// Resolved rather than the raw response: `updateCollection` returns
			// the normalized UI shape, not the raw config this key holds.
			return hexagon;
		},

		onSuccess: hexagon => {
			// Keyed on this collection, so it is always right to write.
			queryClient.setQueryData(configKey, current => current && { ...current, hexagon });

			// The store is not — it holds whichever collection is on screen, and
			// these are mutation-level callbacks, which run even after the panel
			// that started the save has gone. Without this, a save landing after
			// a switch would apply the old collection's settings to the new one,
			// and the next Apply would persist them onto it.
			if (useCollectionStore.getState().hydratedCollectionUuid !== collectionUuid) return;

			// Both fields are applied, not just the patched one, so the store
			// lands on exactly what the collection now holds.
			const applied = readHexagonConfig(hexagon);
			setHexagonResolution(applied.size);
			setHexagonColor(applied.color);
		},

		onError: error => {
			// Nothing moved, so there is nothing to undo — just say why, using
			// the server's own message rather than a generic one.
			addToast({
				id: 'hexagon-settings-error',
				title: 'Could not save hexagon settings',
				message: error?.message,
				bg: 'danger',
				delay: 4000,
			});
		},

		onSettled: () => invalidateCollectionConfig(queryClient, collectionUuid),
	});

	const { mutate } = saveMutation;

	/**
	 * Persist a partial change to the settings, applying it on success.
	 * @param {{ size?: number, color?: string }} patch
	 * @param {object} [options] - per-call React Query callbacks (onSettled, …).
	 *   The colour picker uses it to drop the draft it shows in the meantime.
	 */
	const saveHexagonConfig = useCallback((patch, options) => mutate(patch, options), [mutate]);

	return { saveHexagonConfig, isSaving: saveMutation.isPending };
};
